import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { getCategoriesNameById, getStatusById } from '../utils/helpers';

// Helper function to get floor plan name
const getFloorPlanName = (floorPlanId, floorPlanNamesData) => {
const floorPlan = floorPlanNamesData.find(fp => fp.id == floorPlanId);
return floorPlan ? floorPlan.name : 'N/A';
};

// Helper function to get branch name
const getBranchName = (branchId, branchNamesFilterData) => {
const branch = branchNamesFilterData.find(b => b.id == branchId);
return branch ? branch.name : 'N/A';
};

// Export to CSV
export const exportCountersToCSV = async (data, floorPlanNamesData, branchNamesFilterData) => {
try {
    const fileType = 'text/csv;charset=utf-8;';
    const fileName = `counters_${new Date().toISOString().split('T')[0]}.csv`;
    
    const csvData = data.map(item => ({
    '#': (data.indexOf(item) + 1),
    'Date Created': new Date(item.created_at).toLocaleDateString(),
    'Floor Plan': getFloorPlanName(item.floor_plan_id, floorPlanNamesData),
    'Counter Name': item.name,
    'Description': item.description,
    'Status': getStatusById(item.status),
    'Branch': getBranchName(item.branch_id, branchNamesFilterData)
    }));

    const csvHeaders = Object.keys(csvData[0]);
    const csvRows = csvData.map(row => 
    csvHeaders.map(header => JSON.stringify(row[header] || '')).join(',')
    );
    
    const csvString = [csvHeaders.join(','), ...csvRows].join('\r\n');
    
    const blob = new Blob([csvString], { type: fileType });
    FileSaver.saveAs(blob, fileName);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};

// Export to Excel
export const exportCountersToExcel = async (data, floorPlanNamesData, branchNamesFilterData) => {
try {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const fileName = `counters_${new Date().toISOString().split('T')[0]}`;
    
    const wsData = [
    ['#', 'Date Created', 'Floor Plan', 'Counter Name', 'Description', 'Status', 'Branch'],
    ...data.map((item, index) => [
        index + 1,
        new Date(item.created_at).toLocaleDateString(),
        getFloorPlanName(item.floor_plan_id, floorPlanNamesData),
        item.name,
        item.description,
        getStatusById(item.status),
        getBranchName(item.branch_id, branchNamesFilterData)
    ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = { Sheets: { 'Counters': ws }, SheetNames: ['Counters'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: fileType });
    
    FileSaver.saveAs(dataBlob, fileName + fileExtension);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};

// Export to PDF Enhanced
export const exportCountersToPDFEnhanced = async (data, floorPlanNamesData, branchNamesFilterData, filters = {}) => {
return new Promise((resolve, reject) => {
    try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPos = margin;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text("Counters Report", pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
    
    // Date and time
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, yPos);
    yPos += 8;
    
    // Filters applied
    const filterTexts = [];
    if (filters.name) filterTexts.push(`Name: ${filters.name}`);
    if (filters.dateFrom || filters.dateTo) {
        const dateRange = [];
        if (filters.dateFrom) dateRange.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
        if (filters.dateTo) dateRange.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
        filterTexts.push(`Date Range: ${dateRange.join(' - ')}`);
    }
    if (filters.searchTerm) filterTexts.push(`Search: ${filters.searchTerm}`);
    
    if (filterTexts.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`Filters: ${filterTexts.join(', ')}`, margin, yPos, { maxWidth: pageWidth - 2 * margin });
        yPos += 10;
    }
    
    // Summary
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    const activeCount = data.filter(item => item.status === 1).length;
    const inactiveCount = data.filter(item => item.status === 2).length;
    doc.text(`Total Counters: ${data.length} | Active: ${activeCount} | Inactive: ${inactiveCount}`, margin, yPos);
    yPos += 8;
    
    // Table headers
    const headers = [
        ['No.', 'Date Created', 'Floor Plan', 'Counter Name', 'Description', 'Status', 'Branch']
    ];
    
    const tableData = data.map((item, index) => [
        index + 1,
        new Date(item.created_at).toLocaleDateString(),
        getFloorPlanName(item.floor_plan_id, floorPlanNamesData),
        item.name,
        item.description.length > 50 ? item.description.substring(0, 47) + '...' : item.description,
        getStatusById(item.status),
        getBranchName(item.branch_id, branchNamesFilterData)
    ]);
    
    // Add table
    doc.autoTable({
        startY: yPos,
        head: headers,
        body: tableData,
        theme: 'grid',
        headStyles: {
        fillColor: [79, 70, 229], // Primary color from theme
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
        },
        bodyStyles: {
        fontSize: 9,
        textColor: [50, 50, 50]
        },
        alternateRowStyles: {
        fillColor: [245, 245, 245]
        },
        columnStyles: {
        0: { cellWidth: 15 }, // No.
        1: { cellWidth: 25 }, // Date Created
        2: { cellWidth: 25 }, // Floor Plan
        3: { cellWidth: 30 }, // Counter Name
        4: { cellWidth: 40 }, // Description
        5: { cellWidth: 20 }, // Status
        6: { cellWidth: 25 }  // Branch
        },
        margin: { left: margin, right: margin },
        didDrawPage: function(data) {
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        }
    });
    
    const fileName = `counters_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    resolve();
    } catch (error) {
    reject(error);
    }
});
};

// Export to JSON (optional additional export)
export const exportCountersToJSON = async (data, floorPlanNamesData, branchNamesFilterData) => {
try {
    const fileType = 'application/json';
    const fileName = `counters_${new Date().toISOString().split('T')[0]}.json`;
    
    const jsonData = data.map(item => ({
    ...item,
    floor_plan_name: getFloorPlanName(item.floor_plan_id, floorPlanNamesData),
    branch_name: getBranchName(item.branch_id, branchNamesFilterData),
    status_name: getStatusById(item.status),
    formatted_date: new Date(item.created_at).toLocaleDateString()
    }));

    const jsonString = JSON.stringify(jsonData, null, 2);
    
    const blob = new Blob([jsonString], { type: fileType });
    FileSaver.saveAs(blob, fileName);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};